import * as _ from 'lodash';
import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {Moment} from 'moment-timezone';
import {Shipment, ShipmentJSON} from './shipment';
import {ShipmentRoutesService} from './shipment-routes.service';
import {BehaviorSubject, Observable, of, Subscription, throwError} from 'rxjs';
import {ShipmentEvent} from './shipment-event';
import {ApiResponse, AuthService, BaseService} from '@hassel-it/angular-common';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map} from "rxjs/operators";
import * as moment from "moment/moment";

interface ShipmentPickingList {
    id: number;
    shipments: Shipment[];
    modified: Moment;
    created: Moment;
}

export interface ShipmentMarkerProperties {
    id?: string;
    label_text?: string;
    shipment_route_id?: string;
    shipment_reference?: string;
    icon_image?: string;
    marker_color?: string;
    text_color?: string;
    route_color?: string;
    weight_warning?: boolean;
    weight_sum?: number;
    shipment_lines_count?: number;
    icon?: string;
    direction?: string;
    speed?: number;
    driver_name?: string;
}

export interface BranchShipments {
    paint: any;
    featureCollection: GeoJSON.FeatureCollection<GeoJSON.Point, ShipmentMarkerProperties>;
}

@Injectable()
export class ShipmentsService extends BaseService {

    public paintData: any = {active: false};

    public colors = [
        {
            name: 'gray',
            branch: 'none',
            color: '#CACACA'
        },
        {
            name: 'red',
            branch: 'cla',
            color: '#a21235'
        },
        {
            name: 'violet',
            branch: 'hoe',
            color: '#561859'
        },
        {
            name: 'gold',
            branch: 'rin',
            color: '#ad7522'
        },
        {
            name: 'yellow',
            branch: 'ulf',
            color: '#ae9926'
        },
        {
            name: 'green',
            branch: 'var',
            color: '#0d6413'
        },
        {
            name: 'blue',
            branch: 'vib',
            color: '#0b3567'
        },
        {
            name: 'teal',
            branch: 'vid',
            color: '#0a413b'
        }
    ];

    public countries = [
        {'code': null, 'name': 'Danmark (Standard)'},
        {'code': 'DK', 'name': 'Denmark'},
        {'code': 'SE', 'name': 'Sverige'},
        {'code': 'NO', 'name': 'Norway'},
        {'code': 'FI', 'name': 'Finland'},
        {'code': 'GB', 'name': 'United Kingdom'},
        {'code': 'DE', 'name': 'Tyskland'}
    ];

   /**
     * Get value for bit a given position
     *
     * @param number
     * @param position
     */
    public static getBit(number: number, position: number) {
       // tslint:disable-next-line:no-bitwise
        return (number & (1 << position)) !== 0;
    }

    public colorNameForBranch(branch) {

        const color =
            this.colors.filter((item) => {
                return item.branch === branch;
            }).find(Boolean)
        if (color) {
            return color.name;
            //    @todo return shape instead;
        } else {
            return '#000000'
        }
    }

    public colorForBranch(branch) {

        const color =
            this.colors.filter((item) => {
                return item.branch === branch;
            }).find(Boolean)
        if (color) {
            return color.color;
            //    @todo return shape instead;
        } else {
            return '#000000'
        }
    }


    public deliveryBitMasks = {
        webOrdre: 0,
        idaOrdre: 1,
        ikkePaaLager: 2,
        lagervare: 3,
        bestiltLeverandoer: 4,
        bestiltFjernlager: 5,
        dropShipment: 6,
        plukningStartet: 7,
        ankommet: 8,
        pakket: 9,
        klarTilAfhentning: 10,
        tildeltChauffoer: 11,
        hentetAfFragtUdbyder: 12,
        rutePaabegyndt: 13,
        modtagetAfKunde: 14,
        skadet: 15,
        annulleret: 16,
        boxen: 17,
        boxenRumBooket: 18,
        boxenSmsSendt: 19,
        slaaetIKassen: 20,
        afhentet: 21
    }

    public iconNames = [];

    public shipmentsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(public http: HttpClient, public authService: AuthService, private shipmentRoutesService: ShipmentRoutesService) {
        super(http, authService);

        let i = 3;
        while (i < 10) {
            this.colors.forEach((item) => {

                let y = 0;
                while (y < 21) {
                    this.iconNames.push(i + '-' + item.name + '-' + y.toString());
                    y++;
                }

            });
            i++;
        }
    }

    public getShipmentsUpdatedStream(): Observable<boolean> {
        return this.shipmentsUpdated.asObservable();
    }

    public postShipmentChangedEvent(event) {
        this.shipmentsUpdated.next(event)
    }

    createShipmentEvent(eventData: ShipmentEvent): Promise<ShipmentEvent> {
        return this.http.post(environment.apiBase + '/api/ten-four/shipment-events', eventData, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return ShipmentEvent.fromJSON(response.data);
        }).catch(ShipmentsService.handleError);
    }


    getShipmentEventStates(): Promise<ApiResponse> {
        return this.http.get(environment.apiBase + '/api/ten-four/shipment-events/states', {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return new ApiResponse(response.data, -1);
        }).catch(ShipmentsService.handleError);
    }

    forwardShipmentEventStates(id, data): Promise<ApiResponse> {
        this.reloadHeaders();
        return this.http.post(environment.apiBase + '/api/ten-four/shipment-events/' + id + '/forward', data, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return new ApiResponse(response.data, -1);
        }).catch(ShipmentsService.handleError);
    }

    flashShipment(shipmentId: string, productLines: string[]) {
        return this.http.post(environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/flash',
            {shipment_product_lines: productLines},
            {
                headers: this.httpHeaders
            }).toPromise().then((response: any) => {
            return true
        }).catch(ShipmentsService.handleError);
    }

    getShipment(shipmentId: string, options: GetShipmentsOptions): Promise<Shipment> {

        const params = this.buildParameters(options);

        return this.http.get(environment.apiBase + '/api/ten-four/shipments/' + shipmentId, {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return Shipment.fromJSON(response.data);
        }).catch(ShipmentsService.handleError);
    }

    buildParameters(options: GetShipmentsOptions): HttpParams {
        let params: HttpParams = new HttpParams();

        if (options.valid_coordinates) {
            params = params.set('valid_coordinates', <any>options.valid_coordinates);
        }

        if (options.all) {
            params = params.set('all', <any>options.all);
        }

        let allowSearch = false;

        if (options.order_reference
            || options.nota_number
            || options.rekv_number
            || options.product_number
            || options.contact_information_adress
            || options.purchase_order
            || options.contact_information_name
            || options.contact_information_phone
            || options.contact_information_email
            || options.tracking_number
        ) {
            allowSearch = true;
        }

        if (options.isSearching) {
            params = params.set('sort', 'Shipments.shipping_date');
            params = params.set('direction', 'desc');


        } else {
            if (options.sort) {
                params = params.set('sort', <any>options.sort);
            }

            if (options.direction) {
                params = params.set('direction', <any>options.direction);
            }
        }

        if (allowSearch) {
            if (options.order_reference) {
                params = params.set('order_reference', <any>options.order_reference);
            }

            if (options.rekv_number) {
                params = params.set('rekv_number', <any>options.rekv_number);
            }

            if (options.nota_number) {
                params = params.set('nota_number', <any>options.nota_number);
            }

            if (options.product_number) {
                params = params.set('product_number', <any>options.product_number);
            }

            if (options.purchase_order) {
                params = params.set('purchase_order', <any>options.purchase_order);
            }

            if (options.contact_information_name) {
                params = params.set('contact_information_name', <any>options.contact_information_name);
            }

            if (options.contact_information_adress) {
                params = params.set('contact_information_adress', <any>options.contact_information_adress);
            }

            if (options.contact_information_phone) {
                params = params.set('contact_information_phone', <any>options.contact_information_phone);
            }

            if (options.contact_information_email) {
                params = params.set('contact_information_email', <any>options.contact_information_email);
            }

            if (options.tracking_number) {
                params = params.set('tracking_number', <any>options.tracking_number);
            }

            if (options.search_return) {
                params = params.set('search_return', <any>options.search_return);
            }
        }

        if (options.carrierId) {
            params = params.set('carrier_id', <any>options.carrierId);
        }

        if (options.startDate) {
            params = params.set('startDate', <any>options.startDate.format('YYYY-MM-DD'));
        }

        if (options.endDate) {
            params = params.set('endDate', <any>options.endDate.format('YYYY-MM-DD'));
        }

        if (options.dateField) {
            params = params.set('date_field', <any>options.dateField);
        }

        if (options.statusFilter) {
            params = params.set('status_filter', <any>options.statusFilter);
        }

        if (options.branch && Array.isArray(options.branch)) {
            const branchQuery = options.branch.join(';');
            params = params.set('branch', <any>branchQuery);
        } else if (options.branch) {
            params = params.set('branch', <any>options.branch);
        }


        if (options.combineEvents) {
            params = params.set('combine_events', '1')
        }

        if (options.showTenFourShipment) {
            params = params.set('show_ten_four_shipment', '1')
        }

        if (options.showPostNordShipment) {
            params = params.set('show_post_nord_shipment', '1')
        }

        if (options.showGLSShipment) {
            params = params.set('show_gls_shipment', '1')
        }

        if (options.showDanskeFragtmaendShipment) {
            params = params.set('show_danske_fragtmaend_shipment', '1')
        }


        if (options.showAfhenterShipment) {
            params = params.set('afhenter_shipment', '1')
        }

        if (options.showBoxenShipment) {
            params = params.set('boxen_shipment', '1')
        }

        if (options.showDropship) {
            params = params.set('shop_dropship_shipment', '1')
        }

        if (options.hideDropshipmentProductLines) {
            params = params.set('hideDropshipmentProductLines', '1')
        }

        if (options.emptyBranch) {
            params = params.set('empty_branch', '1')
        }

        if (options.routeId) {
            params = params.set('shipment_route_id', options.routeId);
        }

        if (options.showProductImages) {
            params = params.set('show_product_images', '1');
        }

        return params;
    }

    getShipments(page: number = 1, options: GetShipmentsOptions): Promise<ApiResponse> {
        this.reloadHeaders();
        let params = this.buildParameters(options);
        params = params.set('page', <any>page);
        return this.http.get(environment.apiBase + '/api/ten-four/shipments', {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            const results = _.chain(response.data).map((json: ShipmentJSON): Shipment => {
                return Shipment.fromJSON(json)
            }).value();
            return new ApiResponse(results, response.pagination.count as number);
        }).catch(ShipmentsService.handleError);
    }

    getShipmentsWithMissingCoordinates(page: number = 1, options: GetShipmentsOptions): Promise<ApiResponse> {
        this.reloadHeaders();
        let params = this.buildParameters(options);
        params = params.set('page', <any>page);
        params = params.set('sort', <any>'shipping_date');
        params = params.set('direction', <any>'desc');


        if (options.branch && Array.isArray(options.branch)) {
            const branchQuery = options.branch.join(';');
            params = params.set('branch', <any>branchQuery);
        } else if (options.branch) {
            params = params.set('branch', <any>options.branch);
        }

        return this.http.get(environment.apiBase + '/api/ten-four/shipments/missing_coordinates', {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            const results = _.chain(response.data).map((json: ShipmentJSON): Shipment => {
                return Shipment.fromJSON(json)
            }).value();
            return new ApiResponse(results, response.pagination.count as number);
        }).catch(ShipmentsService.handleError);
    }

    getShipmentStatus(options: GetShipmentsOptions): Promise<ShipmentStatsResponse> {
        this.reloadHeaders();
        const params = this.buildParameters(options);
        return this.http.get(environment.apiBase + '/api/ten-four/shipments/stats', {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return response.data;
        }).catch(ShipmentsService.handleError);
    }

    createShipmentPickingList(data: any): Observable<ShipmentPickingList> {
        this.reloadHeaders();
        return this.http.post(environment.apiBase + '/api/ten-four/shipment-picking-lists/', data, {
            headers: this.httpHeaders
        }).pipe(map((response:any) => {
            return response.data as ShipmentPickingList;
        }));
    }

    createShipment(shipment: Shipment): Promise<Shipment> {
        this.reloadHeaders();

        return this.http.post(environment.apiBase + '/api/ten-four/shipments', shipment, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return response.data as Shipment;
        }).catch(ShipmentsService.handleError);
    }

    updateShipment(shipment: Shipment, options?: SaveShipmentsOptions): Promise<Shipment> {
        return this.updateShipmentPartial(shipment.id, shipment, options);
    }

    copyShipment(shipment: Shipment, asReturn: boolean): Promise<Shipment> {
        this.reloadHeaders();

        let params: HttpParams = new HttpParams();
        params = params.set('sourceId', shipment.id);

        if (asReturn) {
            params = params.set('asReturn', '1');
        }

        return this.http.post(environment.apiBase + '/api/ten-four/shipments', {}, {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return response.data as Shipment;
        }).catch(ShipmentsService.handleError);
    }

    updateShipmentPartial(id: string, data: any, options?: SaveShipmentsOptions): Promise<Shipment> {
        this.reloadHeaders();

        let params: HttpParams = new HttpParams();

        if (options) {
            if (options.print === true) {
                params = params.set('print', '1')
            }
        }

        return this.http.patch(environment.apiBase + '/api/ten-four/shipments/' + id, data, {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return Shipment.fromJSON(response.data)
        }).catch(ShipmentsService.handleError);
    }

    updateShipments(shipments: any[] = [], options?: SaveShipmentsOptions): Promise<ApiResponse> {
        this.reloadHeaders();

        let params: HttpParams = new HttpParams();
        if (options) {
            if (options.print === true) {
                params = params.set('print', '1')
            }
        }

        return this.http.patch(`${environment.apiBase}/api/ten-four/shipments/edit-many`, shipments, {
            headers: this.httpHeaders,
            params: params,
        }).toPromise().then((response: any) => {
            const results = _.chain(response.data).map((shipmentJson: ShipmentJSON): Shipment => {
                return Shipment.fromJSON(shipmentJson)
            }).value();
            return new ApiResponse(results, -1);
        }).catch(ShipmentsService.handleError);
    }


    optimize(shipments: any[] = []): Observable<any> {
        this.reloadHeaders();

        const shipmentIds = shipments.map((shipment) => {
            return shipment.id
        });
        return this.http.post(`${environment.apiBase}/api/ten-four/shipments/optimize`, {shipment_ids: shipmentIds}, {
            headers: this.httpHeaders,
        }).pipe(map((response: any) => {
            console.log(response);
        }));
    }

    exportGpx(shipments: any[] = []): Observable<any> {
        this.reloadHeaders();

        const shipmentIds = shipments.map((shipment) => {
            return shipment.id
        });
        return this.http.post(`${environment.apiBase}/api/ten-four/shipments/export-gpx`, {shipment_ids: shipmentIds}, {
            headers: this.httpHeaders,
        }).pipe(map((response: any) => {
            console.log(response);
            return new Blob([response.data], {type: 'application/xml'});

        }));
    }

    startRoute(shipments: Shipment[]) {
        this.reloadHeaders();

        const shipmentIds = shipments.map((shipment) => {
            return shipment.id
        });
        return this.http.post(`${environment.apiBase}/api/ten-four/shipments/start-route`, {shipment_ids: shipmentIds}, {
            headers: this.httpHeaders,
        }).pipe(map((response: any) => {
            console.log(response);
        }));
    }


    printShipments(shipments: any[] = [], options?: SaveShipmentsOptions): Observable<any> {
        this.reloadHeaders();
        return this.http.post(`${environment.apiBase}/api/ten-four/shipments/print-many`, shipments, {
            headers: this.httpHeaders,
            responseType: 'blob',
        }).pipe(map(response => {
            return new Blob([response], {type: 'application/pdf'});
        }))
    }

    printEndOfDay(carrier: string, branch: string, date: Moment, automatic: boolean = true): Observable<any> {
        this.reloadHeaders();
        const postParams = {
            carrier_code: carrier,
            branch: branch,
            from_time: date.format('YYYY-MM-DD'),
            to_time: date.format('YYYY-MM-DD'),
            automatic: automatic,
        };
        return this.http.post(`${environment.apiBase}/api/ten-four/shipments/end-of-day`, postParams, {
            headers: this.httpHeaders,
            responseType: 'blob',
        }).pipe(map(response => {
            return new Blob([response], {type: 'application/pdf'});
        }))
    }

    printPickingList(id:number): Observable<any> {
        this.reloadHeaders();
        const postParams = {};
        return this.http.post(`${environment.apiBase}/api/ten-four/shipment-picking-lists/${id}/print`, postParams, {
            headers: this.httpHeaders,
            responseType: 'blob',
        }).pipe(map(response => {
            return new Blob([response], {type: 'application/pdf'});
        }))
    }

    getPickingList(id: number): Observable<ShipmentPickingList | null> {
        this.reloadHeaders();
        return this.http.get(`${environment.apiBase}/api/ten-four/shipment-picking-lists/${id}`, {
            headers: this.httpHeaders,
        }).pipe(
            map((response: any) => {
                const extra = {
                    modified: moment(response.data.modified),
                    created: moment(response.data.created)
                };
                return Object.assign(response.data, extra) as ShipmentPickingList;
            }),
            catchError((error) => {
                if (error.status === 404) {
                    return of(null);
                }

                return throwError(() => error);
            })
        );
    }

    getPickingLists(page): Observable<ApiResponse> {

        let params: HttpParams = new HttpParams();
        params = params.append('page', page);

        return this.http.get(`${environment.apiBase}/api/ten-four/shipment-picking-lists`, {
            headers: this.httpHeaders,
            params
        }).pipe(
            map((response: any) => {
                return new ApiResponse(response.data, response.pagination.count as number);
            }),
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }

    deleteShipment(shipment: Shipment): Promise<boolean> {
        this.reloadHeaders();

        return this.http.delete(environment.apiBase + '/api/ten-four/shipments/' + shipment.id, {
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return true
        }).catch(ShipmentsService.handleError);
    }

    searchDawaAddress(address: string, zipcode: number) {
        return this.http.get(`https://dawa.aws.dk/adresser?q=${address}&postnr=${zipcode}&struktur=mini`).toPromise().then((response: any) => {
            return response.json();
        }).catch(ShipmentsService.handleError);
    }

    searchGoogleGeocode(id: string, address: string, zipcode: number) {
        this.reloadHeaders();

        let params: HttpParams = new HttpParams();
        params = params.set('adress', <any>address);
        params = params.set('zipcode', <any>zipcode);

        return this.http.get(`${environment.apiBase}/api/ten-four/shipment-addresses/${id}/lookup`, {
            headers: this.httpHeaders,
            params: params,
        }).toPromise().then((response: any) => {
            return response;
        }).catch(ShipmentsService.handleError);
    }


    public getShipmentAdresses(page: number = 1, options: any): Observable<ApiResponse> {

        this.reloadHeaders();

        let params: HttpParams = new HttpParams();
        params = params.set('page', <any>page);

        return this.http.get(`${environment.apiBase}/api/ten-four/shipment-addresses/`, {
            headers: this.httpHeaders,
            params: params,
        }).pipe(map((response:any) => {
            return new ApiResponse(response.data, response.pagination.count as number);
        }));
    }

    public addShipmentAddress(address: any): Observable<ApiResponse> {
        this.reloadHeaders();
        return this.http.post(`${environment.apiBase}/api/ten-four/shipment-addresses/`, address, {
            headers: this.httpHeaders,
        }).pipe(map((response:any) => {
            return new ApiResponse(response.data, response?.pagination?.count as number);
        }));
    }

    public updateShipmentAddress(address: any): Observable<ApiResponse> {
        this.reloadHeaders();
        return this.http.patch(`${environment.apiBase}/api/ten-four/shipment-addresses/${address.id}/`, address, {
            headers: this.httpHeaders,
        }).pipe(map((response:any) => {
            return new ApiResponse(response.data, response?.pagination?.count as number);
        }));
    }

    public iconSlugForRoute(shipment:Shipment, index?: number) {

        let shape;

        const markerColor = this.colorNameForBranch(shipment.branch) + '-' + (index + 1);
        if (shipment.shipment_route && shipment.shipment_route.driver) {

            switch (shipment.shipment_route.driver) {
                case 'A':
                    shape = 3;
                    break;
                case 'B':
                    shape = 4;
                    break;
                case 'C':
                    shape = 5;
                    break;
                case 'D':
                    shape = 3;
                    break;
                case 'E':
                    shape = 7;
                    break;
                case 'F':
                    shape = 8;
                    break;
                case 'G':
                    shape = 9;
                    break;
            }
        } else {
            shape = 1;
        }

        let iconImage = environment.assetBase + '/assets/img/map-icons/' + shape + '-' + markerColor + '.gif';

        if (shipment.carrier === 'PostNord') {
            iconImage = environment.assetBase + '/assets/img/map-icons-alternate/carrier-postnord.png';
        }
        if (shipment.carrier === 'GLS Denmark A/S') {
            iconImage = environment.assetBase + '/assets/img/map-icons-alternate/carrier-gls.png';
        }
        if (shipment.carrier === 'Danske Fragtmænd') {
            iconImage = environment.assetBase + '/assets/img/map-icons-alternate/carrier-df.png';
        }


        return {iconImage, markerColor}
    }

    GeoJSONFromShipments(allShipments: Shipment[], addNumber: boolean = false, useTripIndex: boolean= false): GeoJSON.FeatureCollection<GeoJSON.Point> {

        const shipments = allShipments;
        const features = [];
        shipments.forEach((shipment: Shipment, index) => {

           const markerStyle = this.iconSlugForRoute(shipment, index);

            let labelText = shipment.nota_number + '';
            if (shipment.order_reference) {
                labelText = labelText + ' (w)';
            }
            if (addNumber) {
                labelText = (index + 1) + ' - ' + labelText
            }

            let textColor;
            if (shipment.shipment_status === 'complete') {
                textColor = '#52b3b6';
            } else if (shipment.shipment_status === 'packing_completed') {
                textColor = '#ff9b5a';
            } else {
                textColor = '#000000'
            }

            if (shipment.map_x !== null && shipment.map_x !== 0.00000000) {
                features.push({
                    'type': 'Feature',
                    'properties': {
                        'id': shipment.id,
                        'label_text': labelText,
                        'shipment_route_id': shipment.shipment_route_id,
                        'shipment_reference': shipment.external_reference ? shipment.external_reference : shipment.nota_number,
                        'icon_image': markerStyle.iconImage,
                        'marker_color': markerStyle.markerColor,
                        'text_color': textColor,
                        'route_color': shipment?.shipment_route?.route_color,
                        'weight_warning': shipment.weight_warning,
                        'weight_sum': shipment.weight_sum,
                        'shipment_lines_count' : shipment?.shipment_product_lines?.length,
                    },
                    'geometry': {
                        'type': 'Point',
                        'coordinates': [shipment.map_x, shipment.map_y]
                    }
                })
            }
        });
        return {
            'type': 'FeatureCollection',
            features: features
        }
    }

    FeatureCollectionFromShipments(allShipments: Shipment[], addNumber: boolean = false): BranchShipments[] {
        const output = [];

        const groups = _.groupBy(allShipments, (item) => {
            return item.branch + '-' + item.shipment_route_id
        });
        Object.keys(groups).forEach((key) => {
            const shipments = groups[key];

            const markerColor = this.colorForBranch(shipments[0].branch);
            const branchShipments: BranchShipments = {
                paint: {
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20,
                        100,
                        30,
                        750,
                        40
                    ],
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#223b53',
                    'circle-color': markerColor
                },
                featureCollection: this.GeoJSONFromShipments(shipments, addNumber)
            };
            output.push(branchShipments);

        });
        return output;
    }

    getBranchesPoints(): BranchShipments[] {

        const output = [];
        this.colors.forEach((item) => {

            if (item.branch !== 'none') {


                const coords: any = this.shipmentRoutesService.getBranchCoords(item.branch);
                const branchShipments: BranchShipments = {
                    paint: {},
                    featureCollection: {
                        'type': 'FeatureCollection',
                        features: [
                            {
                                'type': 'Feature',
                                'properties': {
                                    'icon': 'factory-' + item.name + '.png',
                                    'label_text': item.branch,
                                },
                                'geometry': {
                                    'type': 'Point',
                                    'coordinates': coords.split(',')
                                }
                            }
                        ]
                    }
                };
                output.push(branchShipments);
            }
        });

        return output;

    }

    updateStock(branch: string, shipments: any[]): Promise<boolean> {
        this.reloadHeaders();
        let params: HttpParams = new HttpParams();
        params = params.set('branch', <any>branch);

        return this.http.patch(environment.apiBase + '/api/ten-four/shipments/update-stock', shipments, {
            params: params,
            headers: this.httpHeaders
        }).toPromise().then((response: any) => {
            return true;
        }).catch(ShipmentsService.handleError);
    }

    lookupDroppoint(shipment: Shipment, adress: any): Observable<any> {
        this.reloadHeaders()
        return this.http.post(environment.apiBase + '/api/ten-four/shipments/' + shipment.id + '/shipment_packages/lookup-droppoints', adress,
            {
                headers: this.httpHeaders
            }
        );
    }

    public getIdForPackingType(carrierCode, packageType) {
        const result = this.shipmentPackagings.find((p) => p.carrierCode === carrierCode && p.packageType === packageType);
        if (result) {
            return result.id;
        } else {
            return null;
        }
    }

    public getShipmentPackageFor(carrierCode, flex, business, droppoint, weight: number, country_code:string) {

        // console.log('getShipmentPackageFor', carrierCode, flex, business, droppoint, weight, country_code);
        if (!country_code || country_code === 'DKK' || country_code === 'null' || country_code === '') {
            country_code = 'DK';
        }

        const shipmentPackage = this.shipmentPackagings.find((p) => {
            if (!p.minWeight && p.minWeight !== 0) {
                return false;
            }

            if (
                p.carrierCode === carrierCode &&
                (p.countryCode === undefined || p.countryCode === country_code) &&
                (p.flex === undefined || p.flex === flex) &&
                (p.business === undefined || p.business === business) &&
                (p.droppoint === undefined || p.droppoint === droppoint) &&
                p.minWeight <= weight && p.maxWeight >= weight) {
                return true;
            }

            return false;
        });
        return shipmentPackage;
    }

    public shipmentPackagings = [

        // Pakkeshop
        {
            id: 1,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Pakkeshop, 0-1kg',
            selected: false,
            minWeight: 0,
            maxWeight: 0.99999,
            flex: false,
            business: false,
            droppoint: true,
            price: 41.25,
            countryCode: 'DK'
        },

        {
            id: 2,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Pakkeshop, 1-5kg',
            selected: false,
            minWeight: 1,
            maxWeight: 4.99999,
            flex: false,
            business: false,
            droppoint: true,
            price: 45
        }, {
            id: 3,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Pakkeshop, 5-10kg',
            selected: false,
            minWeight: 5,
            maxWeight: 9.99999,
            flex: false,
            business: false,
            droppoint: true,
            price: 45
        }, {
            id: 4,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Pakkeshop, 10-15kg',
            selected: false,
            minWeight: 10,
            maxWeight: 14.99999,
            flex: false,
            business: false,
            droppoint: true,
            price: 47.5
        }, {
            id: 5,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Pakkeshop, 15-20kg',
            selected: false,
            minWeight: 15,
            maxWeight: 19.99999,
            flex: false,
            business: false,
            droppoint: true,
            price: 47.5
        },

// Erhverv

        {
            id: 6,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 0-1kg',
            selected: false,
            minWeight: 0,
            maxWeight: 0.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 45
        },
        {
            id: 7,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 1-5kg',
            selected: false,
            minWeight: 1,
            maxWeight: 4.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 47.5
        }, {
            id: 8,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 5-10kg',
            selected: false,
            minWeight: 5,
            maxWeight: 9.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 47.5
        }, {
            id: 9,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 10-15kg',
            selected: false,
            minWeight: 10,
            maxWeight: 14.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 51.25
        }, {
            id: 10,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 15-20kg',
            selected: false,
            minWeight: 15,
            maxWeight: 19.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 51.25
        },
        {
            id: 11,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 20-25kg',
            selected: false,
            minWeight: 20,
            maxWeight: 24.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 71.25
        },
        {
            id: 12,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Erhverv, 25-30kg',
            selected: false,
            minWeight: 25,
            maxWeight: 29.99999,
            flex: undefined,
            business: true,
            droppoint: false,
            price: 80
        },


        // Privat
        {
            id: 13,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 0-1kg',
            selected: false,
            minWeight: 0,
            maxWeight: 0.99999,
            flex: false,
            business: false,
            droppoint: false,
            price: 57.5
        },

        {
            id: 14,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 1-5kg',
            selected: false,
            minWeight: 1,
            maxWeight: 4.99999,
            flex: false,
            business: false,
            droppoint: false,
            price: 60
        }, {
            id: 15,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 5-10kg',
            selected: false,
            minWeight: 5,
            maxWeight: 9.99999,
            flex: false,
            business: false,
            droppoint: false,
            price: 66.25
        }, {
            id: 16,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 10-15kg',
            selected: false,
            minWeight: 10,
            maxWeight: 14.99999,
            flex: false,
            business: false,
            droppoint: false,
            price: 72.5
        }, {
            id: 17,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 15-20kg',
            selected: false,
            minWeight: 15,
            maxWeight: 19.99999,
            flex: false,
            business: false,
            droppoint: false,
            price: 76.25
        },
        {
            id: 171,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 20-25kg',
            selected: false,
            minWeight: 20,
            maxWeight: 24.99999,
            flex: false,
            business: false,
            droppoint: false,
            price: 95
        },
        {
            id: 172,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat, 25-30kg',
            selected: false,
            minWeight: 25,
            maxWeight: 29.99999,
            flex: false,
            business: false,
            droppoint: false,
            price:107.5
        },

        // Privat Flex
        {
            id: 18,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 0-1kg',
            selected: false,
            minWeight: 0,
            maxWeight: 0.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 57.5
        },

        {
            id: 19,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 1-5kg',
            selected: false,
            minWeight: 1,
            maxWeight: 4.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 60
        }, {
            id: 20,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 5-10kg',
            selected: false,
            minWeight: 5,
            maxWeight: 9.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 66.25
        }, {
            id: 21,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 10-15kg',
            selected: false,
            minWeight: 10,
            maxWeight: 14.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 72.5
        }, {
            id: 22,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 15-20kg',
            selected: false,
            minWeight: 15,
            maxWeight: 19.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 76.25
        }, {
            id: 23,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 20-25kg',
            selected: false,
            minWeight: 20,
            maxWeight: 24.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 95
        }, {
            id: 24,
            carrierCode: '11',
            packageType: 'GLS',
            description: 'GLS Privat Flex, 25-35kg',
            selected: false,
            minWeight: 25,
            maxWeight: 29.99999,
            flex: true,
            business: false,
            droppoint: false,
            price: 107.5
        },

        ...[
            ['BE', [119.00, 150.00, 190.00, 250.00, 298.00, 395.00, 438.00]],
            ['BG', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['CY', [303.00, 393.00, 549.00, 801.00, 1086.00, 1431.00, 1628.00]],
            ['EE', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['FI', [232.00, 303.00, 355.00, 422.00, 464.00, 621.00, 681.00]],
            ['FR', [189.00, 277.00, 317.00, 380.00, 416.00, 528.00, 592.00]],
            ['FO', [295.00, 328.00, 367.00, 406.00, 447.00, 513.00, 603.00]],
            ['GR', [303.00, 393.00, 549.00, 723.00, 887.00, 1258.00, 1462.00]],
            ['GL', [295.00, 347.00, 443.00, 529.00, 614.00, 785.00, 917.00]],
            ['NL', [119.00, 150.00, 190.00, 250.00, 298.00, 395.00, 438.00]],
            ['IE', [232.00, 303.00, 363.00, 416.00, 479.00, 632.00, 690.00]],
            ['IS', [317.00, 351.00, 430.00, 477.00, 520.00, 624.00, 749.00]],
            ['IT', [194.00, 260.00, 298.00, 368.00, 422.00, 565.00, 621.00]],
            ['HR', [303.00, 393.00, 549.00, 723.00, 887.00, 1258.00, 1462.00]],
            ['LV', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['LT', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['LU', [119.00, 150.00, 190.00, 250.00, 298.00, 395.00, 438.00]],
            ['MT', [303.00, 393.00, 549.00, 801.00, 1086.00, 1431.00, 1628.00]],
            ['NO', [201.00, 245.00, 309.00, 364.00, 425.00, 582.00, 644.00]],
            ['PL', [123.00, 163.00, 203.00, 265.00, 316.00, 413.00, 473.00]],
            ['PT', [232.00, 303.00, 410.00, 506.00, 618.00, 814.00, 859.00]],
            ['RO', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['CH', [137.00, 173.00, 214.00, 278.00, 334.00, 437.00, 492.00]],
            ['SK', [123.00, 163.00, 203.00, 265.00, 316.00, 413.00, 473.00]],
            ['SI', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['ES', [232.00, 303.00, 410.00, 506.00, 618.00, 814.00, 859.00]],
            ['GB', [189.00, 277.00, 317.00, 380.00, 416.00, 528.00, 592.00]],
            ['SE', [68.30, 68.30, 68.30, 74.60, 74.60, 102.96, 102.96]],
            ['CZ', [123.00, 163.00, 203.00, 265.00, 316.00, 413.00, 473.00]],
            ['DE', [119.00, 150.00, 190.00, 250.00, 298.00, 395.00, 438.00]],
            ['HU', [183.00, 248.00, 285.00, 354.00, 402.00, 546.00, 611.00]],
            ['AT', [137.00, 173.00, 214.00, 278.00, 334.00, 437.00, 492.00]]
        ].reduce((items: any[], countryPriceData: any[], countryIndex) => {
            const countryCode = countryPriceData[0];
            const prices = countryPriceData[1];
            const weights = [
                {minWeight: 0, maxWeight: 0.99999},
                {minWeight: 1, maxWeight: 4.99999},
                {minWeight: 5, maxWeight: 9.99999},
                {minWeight: 10, maxWeight: 14.99999},
                {minWeight: 15, maxWeight: 19.99999},
                {minWeight: 20, maxWeight: 24.99999},
                {minWeight: 25, maxWeight: 29.99999}
            ];

            return items.concat(weights.map((weight, weightIndex) => {
                return {
                    id: 1000 + (countryIndex * 10) + weightIndex,
                    carrierCode: '11',
                    packageType: 'GLS',
                    description: 'GLS International, ' + countryCode + ', ' + weight.minWeight + '-' + Math.ceil(weight.maxWeight) + 'kg',
                    selected: false,
                    minWeight: weight.minWeight,
                    maxWeight: weight.maxWeight,
                    flex: false,
                    business: false,
                    droppoint: false,
                    countryCode: countryCode,
                    price: prices[weightIndex]
                };
            }));
        }, [] as any[]),

        // DF
        {
            id: 25,
            carrierCode: '10',
            packageType: 'BDT',
            description: 'Bundt: Max længde 320cm, max 25kg',
            selected: false,
            selectable: true,
            maxWeight: 25,
        },
        {
            id: 26,
            carrierCode: '10',
            packageType: 'FUL',
            description: 'Full load', selected: false,
            selectable: true
        },
        {
            id: 27,
            carrierCode: '10',
            packageType: 'K04',
            description: 'Trillebør, max højde 150 cm, max 15kg',
            selected: false,
            selectable: true,
            maxWeight: 15,
        },
        {
            id: 28,
            carrierCode: '10',
            packageType: 'K05',
            description: 'Pakke max 5 kg, Max længe 150 cm, Max volume 0,20m3', selected: false,
            minWeight: 0,
            maxWeight: 4.99,
            flex: undefined,
            business: undefined,
            droppoint: undefined,
            selectable: true,
        },
        {
            id: 29,
            carrierCode: '10',
            packageType: 'K10',
            description: 'Pakke max 10 kg, Max længe 150 cm, Max volume 0,20m3', selected: false,
            minWeight: 5,
            maxWeight: 9.99,
            flex: undefined,
            business: undefined,
            droppoint: undefined,
            selectable: true,
        },
        {
            id: 30,
            carrierCode: '10',
            packageType: 'K20',
            description: 'Pakke max 20 kg, Max længe 150 cm, Max volume 0,20m3', selected: false,
            minWeight: 10,
            maxWeight: 19.9,
            flex: undefined,
            business: undefined,
            droppoint: undefined,
            selectable: true,
        }, {
            id: 31,
            carrierCode: '10',
            packageType: 'K30',
            description: 'Pakke max 30 kg, Max længe 150 cm, Max volume 0,20m3', selected: false,
            minWeight: 20,
            maxWeight: 29.9,
            flex: undefined,
            business: undefined,
            droppoint: undefined,
            selectable: true,
        },
        {
            id: 32,
            carrierCode: '10',
            packageType: 'PT1',
            description: 'Euro hel palle, 120cmx80cm, max højde 220cm, max 1000kg', selected: false,
            selectable: true,
            maxWeight: 1000
        },
        {
            id: 33,
            carrierCode: '10',
            packageType: 'PL2',
            description: 'Euro havl palle, 80x60cm, max højde 160cm, max 350kg.', selected: false,
            selectable: true,
            maxWeight: 350
        },
        {
            id: 34,
            carrierCode: '10',
            packageType: 'PL4',
            description: 'Euro kvart palle 60x40cm, max højde 120cm, max 200kg.', selected: false,
            selectable: true,
            maxWeight: 200
        },
        {
            id: 35,
            carrierCode: '10',
            packageType: 'PL5',
            description: 'Special palle 100x120cm, max højde 210cm, max 1000kg.', selected: false,
            selectable: true,
            maxWeight: 1000
        },
        {
            id: 36,
            carrierCode: '10',
            packageType: 'PL6',
            description: 'Special palle 120x100cm, max højde 220cm, max 1000kg.', selected: false,
            selectable: true,
            maxWeight: 1000
        },
        {
            id: 37,
            carrierCode: '10',
            packageType: 'PL7',
            description: 'Special palle 240x120cm, max højde 220cm, max 1200kg.', selected: false,
            selectable: true,
            maxWeight: 1200
        },
        {
            id: 38,
            carrierCode: '10',
            packageType: 'PL8',
            description: '100x600cm, max højde 100cm, max 1500kg.', selected: false,
            selectable: true,
            maxWeight: 1500
        },
        {
            id: 39,
            carrierCode: '10',
            packageType: 'C10',
            description: '190x120x100 - 600kg',
            selected: false,
            selectable: true,
            maxWeight: 600
        },
        {
            id: 40,
            carrierCode: '10',
            packageType: 'C11',
            description: 'Redskab, max 10 kg, max  længde 200cm, max bredde 80cm',
            selected: false,
            selectable: true,
            maxWeight: 10
        },
        {
            id: 41,
            carrierCode: '10',
            packageType: 'CLL',
            description: 'CLL',
            selected: false,
            selectable: true,
            maxWeight: 1500
        },
        {
            id: 42,
            carrierCode: '999',
            packageType: '',
            maxWeight: 99999999,
            description: 'Afhenter',
        },
        {
            id: 43,
            carrierCode: '907',
            packageType: '',
            maxWeight: 99999999,
            description: 'Egen bil',
        }

    ].map((shipmentPackaging) => {
        return {
            ...shipmentPackaging,
            countryCode: shipmentPackaging?.countryCode || 'DK'
        };
    });

    shipmentPackagingsForCarrier(carrier_code: string): any[] {
        return this.shipmentPackagings.filter(item => item.carrierCode === carrier_code);
    }

    resetShipment(shipmentId: string) {
        return this.http.post(environment.apiBase + '/api/ten-four/shipments/' + shipmentId + '/reset',
            {},
            {
                headers: this.httpHeaders
            }).toPromise().then((response: any) => {
            return true
        }).catch(ShipmentsService.handleError);
    }

    deleteShipmentAddress(address) {
        this.reloadHeaders();
        return this.http.delete(`${environment.apiBase}/api/ten-four/shipment-addresses/${address.id}/`, {
            headers: this.httpHeaders,
        }).pipe(map((response: any) => {
            return true;
        }));
    }
}

export interface GetShipmentsOptions {
    search_return?:boolean;
    purchase_order?: any;
    statusFilter?: string;
    showProductImages?: boolean;
    startDate?: Moment;
    endDate?: Moment;
    dateField?: string;
    actorId?: number;
    carrierId?: string;
    sort?: string;
    direction?: string;
    order_reference?: string,
    valid_coordinates?: boolean;
    showTenFourShipment?: boolean;
    showPostNordShipment?: boolean;
    showGLSShipment?: boolean;
    showDanskeFragtmaendShipment?: boolean;
    showAfhenterShipment?:boolean;
    showBoxenShipment?:boolean;
    showDropship?: boolean;
    hideDropshipmentProductLines?: boolean;
    all?: boolean;
    branch?: string[];
    emptyBranch?: boolean;
    driver?: string;
    nota_number?: string;
    rekv_number?: string;
    product_number?: number;
    contact_information_name?: string;
    contact_information_adress?: string;
    contact_information_phone?: string;
    contact_information_email?: string;
    tracking_number?: string;
    isSearching?: boolean;
    routeId?: string;
    combineEvents?: boolean;
}

export interface SaveShipmentsOptions {
    print?: boolean
}

export interface ShipmentStatsResponse {
    sum_price_difference: number;
    count: number;
}
